import React, { useCallback, useMemo, useState, useEffect, useRef } from 'react';
import {
  ReactFlow,
  Background,
  Controls,
  Panel,
  useReactFlow
} from 'reactflow';
import 'reactflow/dist/style.css';
import { Button, IconButton, Box, Typography, Tooltip } from '@mui/material';
import { SettingsIcon } from 'lucide-react';
import CodeIcon from '@mui/icons-material/Code';

const getId = () => `dndnode_${Date.now()}`;

export function Flow ({
  selectedFlow,
  nodes,
  edges,
  onNodesChange,
  onEdgesChange,
  onEdgesDelete,
  onConnect,
  nodeTypes,
  edgeTypes,
  setNodes,
  setSelectedNode,
  onNodeClick,
  handleSave,
  openConfigModal,
  handleDiscard,
  isSaving
}) {
  const { screenToFlowPosition, setCenter, fitView } = useReactFlow();

  // NUEVO: flag para saber si ya se centró el flujo para el flujo seleccionado
  const hasCenteredRef = useRef(false);

  // NUEVO: estado para mostrar/ocultar leyenda de output_key
  const [showOutputLegend, setShowOutputLegend] = useState(false);

  const onDragOver = useCallback((event) => {
    event.preventDefault();
    event.dataTransfer.dropEffect = 'move';
  }, []);

  const onDrop = useCallback(
    (event) => {
      event.preventDefault();

      const data = event.dataTransfer.getData('application/reactflow');
      const obj = JSON.parse(data);

      if (!data) {
        return;
      }

      const position = screenToFlowPosition({
        x: event.clientX,
        y: event.clientY,
      });

      const newNode = {
        id: getId(),
        type: obj.type, // Ajusta el tipo según tus necesidades
        position,
        data: obj.data,
      };

      setNodes((nds) => nds.concat(newNode));
    },
    [screenToFlowPosition, setNodes]
  );

  // NUEVO: memo para calcular la leyenda de output_key a partir de los nodos
  const outputLegendItems = useMemo(
    () => (nodes || [])
      .map((node) => {
        const outputKey = node.data?.OutputKey || node.data?.output_key;
        if (!outputKey) return null;
        return { key: outputKey, nodeId: node.id };
      })
      .filter(Boolean),
    [nodes]
  );

  // NUEVO: al seleccionar un flujo o cambiar los nodos, centralizar la vista
  useEffect(() => {
    // cuando cambia el flujo, reseteamos el flag
    hasCenteredRef.current = false;
  }, [selectedFlow]);

  useEffect(() => {
    if (!selectedFlow || !nodes || nodes.length === 0) return;
    if (hasCenteredRef.current) return; // ya se centró para este flujo

    // buscar nodo de inicio si existe
    const startNode = nodes.find(
      (n) => n.type === 'start' || n.type === 'startcampaign'
    );

    if (startNode && startNode.position) {
      const { x, y } = startNode.position;
      setCenter(x, y, { zoom: 1.0, duration: 500 });
    } else {
      try {
        fitView({ padding: 0.2, duration: 500 });
      } catch (_) {
        // ignorar errores silenciosamente
      }
    }

    // marcar como ya centrado para que no se repita
    hasCenteredRef.current = true;
  }, [selectedFlow, nodes, setCenter, fitView]);

  // NUEVO: handler para seleccionar y centrar nodo desde la leyenda
  const handleSelectFromLegend = useCallback((nodeId) => {
    const node = (nodes || []).find((n) => n.id === nodeId);
    if (!node) return;

    // seleccionar nodo en el estado del padre
    if (setSelectedNode) {
      setSelectedNode(node);
    }

    // centrar el viewport en el nodo
    if (setCenter && node.position) {
      const { x, y } = node.position;
      setCenter(x, y, {
        zoom: 1.5,
        duration: 500,
      });
    }
  }, [nodes, setSelectedNode, setCenter]);

  // NUEVO: handler para copiar output_key con llaves {{key}}
  const handleCopyOutputKey = useCallback(async (key) => {
    const value = `{{${key}}}`;
    try {
      await navigator.clipboard.writeText(value);
    } catch {
      const el = document.createElement('textarea');
      el.value = value;
      document.body.appendChild(el);
      el.select();
      document.execCommand('copy');
      document.body.removeChild(el);
    }
  }, []);

  return (
    <ReactFlow
      nodes={nodes}
      edges={edges}
      onNodesChange={onNodesChange}
      onEdgesChange={onEdgesChange}
      onEdgesDelete={onEdgesDelete}
      onConnect={onConnect}
      nodeTypes={nodeTypes}
      edgeTypes={edgeTypes}
      onDragOver={onDragOver}
      onDrop={onDrop}
      onNodeClick={(_, node) => onNodeClick(_, node)}
      fitView
    >
      <Background />
      <Controls />

      {/* Panel superior con botones + pestaña de leyenda */}
      {selectedFlow && (
        <Panel position="top" style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
          {/* Fila de botones Limpiar / Guardar / Configuración */}
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button variant="outlined" onClick={handleDiscard}>Limpiar</Button>
            <Button variant="contained" onClick={handleSave} disabled={!!isSaving}>Guardar</Button>
            <IconButton size="small" onClick={openConfigModal} disabled={!!isSaving}>
              <SettingsIcon />
            </IconButton>
          </Box>

          {/* Bloque de leyenda alineado a la izquierda */}
          <Box sx={{ display: 'flex', justifyContent: 'flex-start' }}>
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
              <Tooltip title="Ver leyenda de output_key">
                <IconButton
                  size="small"
                  onClick={() => setShowOutputLegend((prev) => !prev)}
                  aria-label="Ver leyenda de output_key"
                >
                  <CodeIcon fontSize="inherit" />
                </IconButton>
              </Tooltip>

              {/* Leyenda tipo cuadro: output_key | Nodo */}
              {showOutputLegend && (
                <Box
                  sx={{
                    mt: 0.5,
                    p: 1,
                    borderRadius: 1,
                    border: '1px solid rgba(0,0,0,0.12)',
                    bgcolor: 'background.paper',
                    maxHeight: 180,
                    overflowY: 'auto',
                    minWidth: 260,
                  }}
                >
                  <Typography variant="caption" sx={{ fontWeight: 600, display: 'block', mb: 0.5 }}>
                    Leyenda de output_key
                  </Typography>

                  {outputLegendItems.length === 0 ? (
                    <Typography variant="caption" color="text.secondary">
                      No hay output_key configurados en los nodos del flujo.
                    </Typography>
                  ) : (
                    <Box>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                        <Typography variant="caption" sx={{ fontWeight: 600 }}>
                          output_key
                        </Typography>
                        <Typography variant="caption" sx={{ fontWeight: 600 }}>
                          Nodo
                        </Typography>
                      </Box>
                      {outputLegendItems.map((item) => (
                        <Box
                          key={`${item.key}-${item.nodeId}`}
                          sx={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            py: 0.25,
                            borderTop: '1px solid rgba(0,0,0,0.04)',
                          }}
                        >
                          {/* Columna output_key: copia {{key}} al hacer click */}
                          <Typography
                            variant="caption"
                            sx={{ mr: 1, cursor: 'pointer', '&:hover': { textDecoration: 'underline' } }}
                            onClick={() => handleCopyOutputKey(item.key)}
                          >
                            {item.key}
                          </Typography>

                          {/* Columna Nodo: centra y selecciona nodo al hacer click */}
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{ cursor: 'pointer', '&:hover': { textDecoration: 'underline' } }}
                            onClick={() => handleSelectFromLegend(item.nodeId)}
                          >
                            {item.nodeId}
                          </Typography>
                        </Box>
                      ))}
                    </Box>
                  )}
                </Box>
              )}
            </Box>
          </Box>
        </Panel>
      )}

    </ReactFlow>
  );
}
